import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ACTIVITY_STATUSES } from '@sparkapp/shared-types';
import type { ActivityFilters, ActivityStatus } from '../types/activity';

const STORAGE_KEY = 'sparkadmin_activity_filters';

const DEFAULT_FILTERS: ActivityFilters = {
  search: '',
  domainId: null,
  skillId: null,
  playTypeId: null,
  physicalDeckId: null,
  preferenceId: null,
  status: null,
};

const ID_KEYS = ['domainId', 'skillId', 'playTypeId', 'physicalDeckId', 'preferenceId'] as const;

function parseId(searchParams: URLSearchParams, key: string): number | null {
  const raw = searchParams.get(key);
  if (!raw) return null;
  const parsed = parseInt(raw, 10);
  return isNaN(parsed) ? null : parsed;
}

function parseFiltersFromURL(searchParams: URLSearchParams): Partial<ActivityFilters> {
  const filters: Partial<ActivityFilters> = {};

  const search = searchParams.get('search');
  if (search) filters.search = search;

  const status = searchParams.get('status');
  if (status && (ACTIVITY_STATUSES as readonly string[]).includes(status)) {
    filters.status = status as ActivityStatus;
  }

  for (const key of ID_KEYS) {
    const id = parseId(searchParams, key);
    if (id !== null) filters[key] = id;
  }

  return filters;
}

function loadFiltersFromStorage(): Partial<ActivityFilters> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

function saveFiltersToStorage(filters: ActivityFilters): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  } catch {
    // ignore
  }
}

function filtersToSearchParams(filters: ActivityFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.search) params.set('search', filters.search);
  if (filters.status) params.set('status', filters.status);
  for (const key of ID_KEYS) {
    if (filters[key]) params.set(key, String(filters[key]));
  }
  return params;
}

export function useActivityFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState<ActivityFilters>(() => {
    const fromUrl = parseFiltersFromURL(searchParams);
    // A deep link must not inherit a stale saved search/skill, so the URL wins outright.
    const hasUrlFilters = Object.keys(fromUrl).length > 0;
    return { ...DEFAULT_FILTERS, ...(hasUrlFilters ? {} : loadFiltersFromStorage()), ...fromUrl };
  });

  const updateFilter = useCallback(
    <K extends keyof ActivityFilters>(key: K, value: ActivityFilters[K]) => {
      setFilters((prev) => {
        const updated = { ...prev, [key]: value };
        setSearchParams(filtersToSearchParams(updated), { replace: true });
        saveFiltersToStorage(updated);
        return updated;
      });
    },
    [setSearchParams],
  );

  const clearFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setSearchParams({}, { replace: true });
    saveFiltersToStorage(DEFAULT_FILTERS);
  }, [setSearchParams]);

  return { filters, updateFilter, clearFilters };
}
